import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { exportToXLS, exportToCSV, getCellValue } from "../utils/exportHelper";

const COLUMN_CATEGORIES = [
    {
        id: "personal",
        titleKey: "Pages.UsersControl.export.category.personal",
        defaultTitle: "المعلومات الشخصية للاتصال",
        icon: "fad fa-address-card",
        color: "#3b82f6",
        bgColor: "#eff6ff",
        columns: [
            { key: "Name", labelKey: "Pages.UsersControl.export.fields.name", icon: "fal fa-user", defaultChecked: true },
            { key: "Email", labelKey: "Pages.UsersControl.export.fields.email", icon: "fal fa-envelope", defaultChecked: true },
            { key: "Phone", labelKey: "Pages.UsersControl.export.fields.phone", icon: "fal fa-phone", defaultChecked: true },
            { key: "Country", labelKey: "Pages.UsersControl.export.fields.country", icon: "fal fa-globe-asia", defaultChecked: true }
        ]
    },
    {
        id: "account",
        titleKey: "Pages.UsersControl.export.category.account",
        defaultTitle: "بيانات ومستوى الحساب",
        icon: "fad fa-user-shield",
        color: "#10b981",
        bgColor: "#ecfdf5",
        columns: [
            { key: "Username", labelKey: "Pages.UsersControl.export.fields.username", icon: "fal fa-at", defaultChecked: true },
            { key: "UserType", labelKey: "Pages.UsersControl.export.fields.userType", icon: "fal fa-user-tag", defaultChecked: true },
            { key: "School", labelKey: "Pages.UsersControl.export.fields.school", icon: "fal fa-school", defaultChecked: true },
            { key: "Status", labelKey: "Pages.UsersControl.export.fields.status", icon: "fal fa-shield-check", defaultChecked: true }
        ]
    },
    {
        id: "activity",
        titleKey: "Pages.UsersControl.export.category.activity",
        defaultTitle: "سجل تواريخ النشاط",
        icon: "fad fa-analytics",
        color: "#8b5cf6",
        bgColor: "#f5f3ff",
        columns: [
            { key: "LastLogin", labelKey: "Pages.Profile.LastLogin.title", icon: "fal fa-history", defaultChecked: false },
            { key: "ActivateUntilDate", labelKey: "Pages.Profile.ActivateUntilDate.title", icon: "fal fa-calendar-star", defaultChecked: false },
            { key: "CreationDate", labelKey: "Pages.Lessons.CreationDate", icon: "fal fa-calendar-plus", defaultChecked: false }
        ]
    }
];

const ALL_COLUMNS = COLUMN_CATEGORIES.flatMap(cat => cat.columns);

const DownloadModal = ({ show, onClose, data, t }) => {
    const [selectedKeys, setSelectedKeys] = useState(
        ALL_COLUMNS.filter(col => col.defaultChecked).map(col => col.key)
    );
    const [format, setFormat] = useState("xls"); // xls, csv, pdf

    if (!show) return null;

    const handleCheckboxChange = (key) => {
        if (selectedKeys.includes(key)) {
            setSelectedKeys(selectedKeys.filter(k => k !== key));
        } else {
            setSelectedKeys([...selectedKeys, key]);
        }
    };

    const handleSelectAll = () => {
        if (selectedKeys.length === ALL_COLUMNS.length) {
            setSelectedKeys([]);
        } else {
            setSelectedKeys(ALL_COLUMNS.map(col => col.key));
        }
    };

    const handleDownload = () => {
        if (selectedKeys.length === 0) {
            alert(t("Pages.Register.error_AllInformationIsRequired_toast.Title") || "الرجاء اختيار حقل واحد على الأقل للتصدير");
            return;
        }

        const activeColumns = ALL_COLUMNS.filter(col => selectedKeys.includes(col.key)).map(col => ({
            key: col.key,
            label: t(col.labelKey)
        }));

        const filename = `users_export_${new Date().toISOString().slice(0, 10)}`;

        if (format === "xls") {
            exportToXLS(data, activeColumns, filename, t);
            onClose();
        } else if (format === "csv") {
            exportToCSV(data, activeColumns, filename, t);
            onClose();
        } else if (format === "pdf") {
            triggerPDFPrint(data, activeColumns, filename);
            onClose();
        }
    };

    const triggerPDFPrint = (exportData, exportCols, filename) => {
        const printContainer = document.createElement("div");
        printContainer.id = "printable-users-table";
        printContainer.style.direction = "rtl";
        printContainer.style.padding = "20px";
        printContainer.style.fontFamily = "system-ui, -apple-system, sans-serif";

        const title = document.createElement("h2");
        title.innerText = t("Pages.UsersControl.export.title") || "تصدير بيانات المستخدمين";
        title.style.textAlign = "center";
        title.style.marginBottom = "20px";
        printContainer.appendChild(title);

        const table = document.createElement("table");
        table.style.width = "100%";
        table.style.borderCollapse = "collapse";
        table.style.fontSize = "12px";

        // Table Header
        const thead = document.createElement("thead");
        const headerRow = document.createElement("tr");
        exportCols.forEach(col => {
            const th = document.createElement("th");
            th.innerText = col.label;
            th.style.border = "1px solid #ddd";
            th.style.padding = "8px";
            th.style.backgroundColor = "#f2f2f2";
            th.style.textAlign = "right";
            headerRow.appendChild(th);
        });
        thead.appendChild(headerRow);
        table.appendChild(thead);

        // Table Body
        const tbody = document.createElement("tbody");
        exportData.forEach(item => {
            const row = document.createElement("tr");
            exportCols.forEach(col => {
                const td = document.createElement("td");
                td.innerText = getCellValue(item, col.key, t);
                td.style.border = "1px solid #ddd";
                td.style.padding = "8px";
                td.style.textAlign = "right";
                row.appendChild(td);
            });
            tbody.appendChild(row);
        });
        table.appendChild(tbody);
        printContainer.appendChild(table);

        const style = document.createElement("style");
        style.innerText = `
            @media print {
                body > * {
                    display: none !important;
                }
                #printable-users-table {
                    display: block !important;
                }
                #printable-users-table, #printable-users-table * {
                    visibility: visible !important;
                }
            }
        `;

        document.body.appendChild(printContainer);
        document.head.appendChild(style);

        setTimeout(() => {
            window.print();
            document.body.removeChild(printContainer);
            document.head.removeChild(style);
        }, 300);
    };

    return (
        <Modal show={show} onHide={onClose} centered size="lg" className="admin-history-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="w-100">
                    <div className="d-flex align-items-center">
                        <div className="history-modal-icon-wrapper ml-3 text-primary" style={{ background: '#f5f3ff', color: '#8b5cf6' }}>
                            <i className="fad fa-file-export"></i>
                        </div>
                        <div className="text-right">
                            <div className="h5 mb-1 font-weight-bold text-dark">{t("Pages.UsersControl.export.title")}</div>
                            <div className="small text-muted">{t("Pages.UsersControl.export.selectColumns")}</div>
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-right pt-3 pb-4">
                {/* Format selection */}
                <div className="mb-4">
                    <div className="text-muted small font-weight-bold mb-2">
                        <i className="fad fa-cog ml-1 text-primary"></i>
                        {t("Pages.UsersControl.export.format")}
                    </div>
                    <div className="d-flex gap-3 justify-content-start flex-wrap">
                        <button
                            type="button"
                            className={`download-format-btn ${format === "xls" ? "active" : ""}`}
                            onClick={() => setFormat("xls")}
                            style={{
                                flex: '1',
                                minWidth: '150px',
                                height: '50px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                background: format === "xls" ? '#92278f' : '#ffffff',
                                color: format === "xls" ? '#ffffff' : '#475569',
                                fontWeight: '600',
                                transition: 'all 0.2s ease',
                                outline: 'none'
                            }}
                        >
                            <i className="fal fa-file-excel ml-2"></i>
                            {t("Pages.UsersControl.export.xls")}
                        </button>
                        <button
                            type="button"
                            className={`download-format-btn ${format === "csv" ? "active" : ""}`}
                            onClick={() => setFormat("csv")}
                            style={{
                                flex: '1',
                                minWidth: '150px',
                                height: '50px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                background: format === "csv" ? '#92278f' : '#ffffff',
                                color: format === "csv" ? '#ffffff' : '#475569',
                                fontWeight: '600',
                                transition: 'all 0.2s ease',
                                outline: 'none'
                            }}
                        >
                            <i className="fal fa-file-csv ml-2"></i>
                            {t("Pages.UsersControl.export.txt")}
                        </button>
                        <button
                            type="button"
                            className={`download-format-btn ${format === "pdf" ? "active" : ""}`}
                            onClick={() => setFormat("pdf")}
                            style={{
                                flex: '1',
                                minWidth: '150px',
                                height: '50px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                background: format === "pdf" ? '#92278f' : '#ffffff',
                                color: format === "pdf" ? '#ffffff' : '#475569',
                                fontWeight: '600',
                                transition: 'all 0.2s ease',
                                outline: 'none'
                            }}
                        >
                            <i className="fal fa-file-pdf ml-2"></i>
                            {t("Pages.UsersControl.export.pdf")}
                        </button>
                    </div>
                    {format === "xls" && (
                        <div className="alert alert-warning border-0 d-flex align-items-start gap-2 py-2 px-3 mt-3" style={{ borderRadius: '10px', fontSize: '0.82rem', lineHeight: '1.4', backgroundColor: '#fffbeb', color: '#b45309', direction: 'rtl', textAlign: 'right' }}>
                            <i className="fad fa-exclamation-triangle mt-1 ml-2" style={{ fontSize: '0.95rem' }}></i>
                            <span>{t("Pages.UsersControl.export.xlsWarning")}</span>
                        </div>
                    )}
                </div>

                {/* Columns Selection */}
                <div className="mb-1">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="text-muted small font-weight-bold">
                            <i className="fad fa-list-ul ml-1 text-primary"></i>
                            {t("Pages.UsersControl.export.selectColumns")}
                        </div>
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            style={{ color: '#92278f', fontWeight: '600', fontSize: '0.88rem' }}
                            onClick={handleSelectAll}
                        >
                            {selectedKeys.length === ALL_COLUMNS.length ? t("Pages.UsersControl.export.deselectAll") : t("Pages.UsersControl.export.selectAll")}
                        </button>
                    </div>

                    <div className="d-flex flex-column gap-3">
                        {COLUMN_CATEGORIES.map(category => (
                            <div key={category.id} className="p-3 mb-3" style={{ border: '1px solid #f1f5f9', backgroundColor: '#f8fafc', borderRadius: '16px' }}>
                                <div className="d-flex align-items-center mb-3 font-weight-bold" style={{ color: '#1e293b', fontSize: '0.95rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                                    <div className="d-flex align-items-center justify-content-center ml-2" style={{ width: '28px', height: '28px', borderRadius: '8px', background: category.bgColor, color: category.color }}>
                                        <i className={`${category.icon}`}></i>
                                    </div>
                                    <span>{t(category.titleKey) || category.defaultTitle}</span>
                                </div>
                                <div className="row">
                                    {category.columns.map(col => {
                                        const isChecked = selectedKeys.includes(col.key);
                                        return (
                                            <div key={col.key} className="col-md-6 mb-2">
                                                <div
                                                    className="column-checkbox-item"
                                                    onClick={() => handleCheckboxChange(col.key)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '12px 16px',
                                                        borderRadius: '12px',
                                                        background: isChecked ? '#fdf4ff' : '#ffffff',
                                                        border: isChecked ? '1px solid #92278f' : '1px solid #e2e8f0',
                                                        boxShadow: isChecked ? '0 2px 8px rgba(146, 39, 143, 0.08)' : 'none',
                                                        transition: 'all 0.2s ease',
                                                        userSelect: 'none'
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i className={`${col.icon} ml-2 ${isChecked ? 'text-primary' : 'text-muted'}`} style={{ color: isChecked ? '#92278f' : '#64748b', fontSize: '1rem', width: '20px', textAlign: 'center' }}></i>
                                                        <span style={{ fontSize: '0.9rem', color: isChecked ? '#1e293b' : '#64748b', fontWeight: isChecked ? '600' : 'normal' }}>
                                                            {t(col.labelKey)}
                                                        </span>
                                                    </div>

                                                    {/* Modern Custom Checkbox Box */}
                                                    <div style={{
                                                        width: '20px',
                                                        height: '20px',
                                                        borderRadius: '6px',
                                                        border: isChecked ? '2px solid #92278f' : '2px solid #cbd5e1',
                                                        background: isChecked ? '#92278f' : 'transparent',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        transition: 'all 0.15s ease'
                                                    }}>
                                                        {isChecked && <i className="fas fa-check text-white" style={{ fontSize: '0.75rem' }}></i>}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 justify-content-between">
                <button type="button" className="btn btn-light px-4 py-2" style={{ borderRadius: '10px', fontWeight: '600', width: 'auto' }} onClick={onClose}>
                    {t("Cancel")}
                </button>
                <button
                    type="button"
                    className="btn btn-primary px-4 py-2 d-flex align-items-center gap-2 shadow-sm border-0"
                    onClick={handleDownload}
                    style={{ borderRadius: '10px', fontWeight: '600', backgroundColor: '#92278f', width: 'auto' }}
                >
                    <i className="fal fa-file-download ml-2"></i>
                    {t("Pages.UsersControl.export.download")}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default DownloadModal;
